<template>
  <div class="section-image">
    <div class="section-image__header" @click="$emit('toggle')">
      <i class="mdi mdi-drag section-image__drag-handle" />
      <AppLabel color="info" text="画像セクション" />
      <span class="section-image__spacer" />
      <i :class="collapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" />
    </div>

    <div v-if="collapsed && section.pc_media_url" class="section-image__preview">
      <img :src="section.pc_media_url" class="section-image__preview-image" />
    </div>

    <div v-if="!collapsed" class="section-image__content">
      <div class="section-image__field">
        <div class="label section-image__label-row">
          <span>PC画像</span>
          <el-tooltip placement="top">
            <div slot="content">
              デバイスの横幅が768px以上の場合、PC画像が表示されます。<br />
              アップロード可能最大サイズ: 画像5MBです。
            </div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
          <span class="badge badge-danger">必須</span>
        </div>
        <div>
          <div :class="['section-image__upload', { 'section-image__upload--disabled': disabled }]">
            <ImageUpload
              :image-url="section.pc_media_url || null"
              :image-origin-name="section.pc_media_name || null"
              :is-loading="isPcLoading"
              @file-change="handleMediaChange('pc', $event)"
              @remove-media="handleRemoveMedia('pc')"
            />
          </div>
        </div>
      </div>

      <div class="section-image__field">
        <div class="label section-image__label-row">
          <span>横幅サイズ(PC)</span>
          <el-tooltip placement="top">
            <div slot="content">デバイスの横幅が768px以上の場合の最大横幅サイズを設定できます。</div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
          <span class="badge badge-danger">必須</span>
        </div>
        <el-input
          :class="{ 'section-image__input-error': isPcMediaSizeEmpty }"
          :value="pcMediaSizeInput"
          :maxlength="4"
          size="small"
          placeholder="800px"
          :disabled="disabled"
          @input="handlePcMediaSizeInput"
        >
          <span slot="suffix" class="section-image__px-label">px</span>
        </el-input>
        <div v-if="isPcMediaSizeEmpty" class="section-image__error">横幅サイズを入力してください。</div>
      </div>

      <div class="section-image__field">
        <div class="label section-image__label-row">
          <span>SP画像</span>
          <el-tooltip placement="top">
            <div slot="content">
              デバイスの横幅が767px以下の場合、SP画像が表示されます。<br />
              アップロード可能最大サイズ: 画像5MBです。
            </div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
          <span class="badge badge-danger">必須</span>
        </div>
        <div>
          <div :class="['section-image__upload', { 'section-image__upload--disabled': disabled }]">
            <ImageUpload
              :image-url="section.sp_media_url || null"
              :image-origin-name="section.sp_media_name || null"
              :is-loading="isSpLoading"
              @file-change="handleMediaChange('sp', $event)"
              @remove-media="handleRemoveMedia('sp')"
            />
          </div>
        </div>
      </div>

      <div class="section-image__field">
        <div class="label section-image__label-row">
          <span>リンクURL</span>
          <el-tooltip placement="top">
            <div slot="content">画像をクリックしたときのURLを設定できます。</div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
        </div>

        <el-input
          :value="section.url"
          size="small"
          placeholder="https://"
          :disabled="disabled"
          @input="updateField('url', $event)"
        />
        <div v-if="isUrlInvalid" class="section-image__error">URLはhttp(s)で始まる必要があります。</div>
      </div>

      <div class="section-image__field">
        <div class="label mb-x1">
          <span>色の設定</span>
        </div>
        <el-switch :value="isBackgroundEnabled" :disabled="disabled" @change="toggleBackgroundColor" />
      </div>

      <div v-if="isBackgroundEnabled" class="section-image__field">
        <div class="label">
          <span>背景色</span>
        </div>

        <ColorPicker
          :value="background_color_display"
          :disabled="disabled"
          :showHex="showHex"
          @input="updateField('background_color', $event)"
        />
      </div>

      <div class="section-image__actions">
        <el-button plain :disabled="!canDelete || disabled" @click="handleDelete">
          <i class="mdi mdi-delete-forever" />
          削除
        </el-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import AppLabel from '@/client/components/_atoms/app_label'
import ColorPicker from '@/client/components/_atoms/color_picker'
import ImageUpload from '@/client/components/_atoms/image_upload'
import { ImageSectionData } from '@/client/components/_molecules/section_image/types'

import { parsePcMediaSizeInput } from './section_image.utils'

type MediaField = 'pc' | 'sp'

export default defineComponent({
  name: 'SectionImage',
  components: {
    AppLabel,
    ImageUpload,
    ColorPicker
  },
  props: {
    section: {
      type: Object as PropType<ImageSectionData>,
      required: true
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    isPcLoading: {
      type: Boolean,
      default: false
    },
    isSpLoading: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showHex: {
      type: Boolean,
      default: false
    },
    bodyBackgroundColor: {
      type: String,
      default: '#ffffff'
    }
  },
  emits: ['toggle', 'update', 'delete', 'media-change', 'media-remove'],
  setup(props, { emit }) {
    const pcMediaSizeInput = computed(() => (props.section.pc_media_size ? String(props.section.pc_media_size) : ''))
    const isPcMediaSizeEmpty = computed(() => props.section.pc_media_size === 0)
    const isUrlInvalid = computed(() => {
      const url = props.section.url
      if (!url) {
        return false
      }
      return !/^https?:\/\//.test(url)
    })
    const isBackgroundEnabled = ref(props.section.background_color !== null)
    const background_color_display = computed(() => props.section.background_color ?? '')

    watch(
      () => props.section.background_color,
      value => {
        isBackgroundEnabled.value = value !== null
      }
    )

    const updateField = (field: keyof ImageSectionData, value: unknown) => {
      emit('update', { ...props.section, [field]: value })
    }

    const handlePcMediaSizeInput = (value: string) => {
      if (props.disabled) {
        return
      }
      const parsed = parsePcMediaSizeInput(value)
      updateField('pc_media_size', parsed ?? 0)
    }

    const handleMediaChange = (field: MediaField, event: Event) => {
      if (props.disabled) {
        return
      }
      const target = event.target as HTMLInputElement
      const file = target.files && target.files[0]
      if (!file) {
        return
      }

      emit('media-change', { field, file })
    }

    const handleRemoveMedia = (field: MediaField) => {
      if (props.disabled) {
        return
      }
      emit('media-remove', { field })
    }

    const handleDelete = () => {
      if (!props.canDelete || props.disabled) {
        return
      }
      emit('delete')
    }

    const toggleBackgroundColor = (value: boolean) => {
      if (props.disabled) {
        return
      }
      isBackgroundEnabled.value = value
      updateField('background_color', value ? props.bodyBackgroundColor : null)
    }

    return {
      pcMediaSizeInput,
      isPcMediaSizeEmpty,
      isUrlInvalid,
      isBackgroundEnabled,
      background_color_display,
      updateField,
      handlePcMediaSizeInput,
      handleMediaChange,
      handleRemoveMedia,
      handleDelete,
      toggleBackgroundColor
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.mdi-help-circle {
  margin: 0;
  font-size: 18px;
}

// グローバルデザインだと崩れるため、display: inline-block に変更
.badge.badge-danger {
  position: initial;
  display: inline-block;
}

.label {
  display: flex;
  align-items: center;
}

.section-image {
  background: $text-color-white;
  border: 1px solid $border-color;
  border-radius: $round;

  &__header {
    display: flex;
    gap: $spacing-unit;
    align-items: center;
    padding: $spacing-unit * 2;
    cursor: pointer;
    -webkit-user-select: none;
    user-select: none;
  }

  &__drag-handle {
    font-size: 18px;
    color: $sub-text-color;
    cursor: grab;
  }

  &__spacer {
    flex: 1;
  }

  &__preview {
    padding: $spacing-unit * 2;
  }

  &__preview-image {
    max-width: 100%;
    max-height: $spacing-unit * 32;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit * 2;
    padding: $spacing-unit * 2;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit;
  }

  &__label-row {
    display: inline-flex;
    flex-wrap: wrap;
    gap: $spacing-unit;
    align-items: center;
    max-width: 100%;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__error {
    margin-top: $spacing-unit;
    color: $danger-color;
  }

  &__input-error:deep(.el-input__inner) {
    border-color: $danger-color;
  }

  &__input-error:deep(.el-input__inner:focus) {
    border-color: $danger-color;
  }

  &__px-label {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 100%;
  }

  &__upload--disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}
</style>
